import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

import { useContainer } from "@contexts/container-context";
import { useCurrentUser } from "@hooks/queries/account";
import { parseStringBoolean } from "@lib/browser-utils";
import { ConfigurationKeys } from "@services";

import { useOrganizationDetails } from "./organization";

const isCourseBuilderConfigOn = (data) =>
  parseStringBoolean(
    data?.config_settings?.find(
      (s) => s.name === ConfigurationKeys.COURSE_BUILDER_ENABLED
    )?.value
  );

/**
 * Reads the org's `course-builder-enabled` flag from the config-service
 * (DASHBOARD app). For a parent org the flag is true when ANY of its child
 * orgs has it enabled (the parent's own value is ignored); a single org uses
 * its own value. Returns the boolean value (false until loaded).
 */
export function useCourseBuilderConfigEnabled(options = {}) {
  const { configurationService } = useContainer();
  const currentUser = useCurrentUser();
  const orgCode = currentUser?.organizationCode;

  const { data: organization } = useOrganizationDetails(orgCode, {
    enabled: !!orgCode,
  });

  // Parent org → check only its children; otherwise the org itself.
  const orgIds = useMemo(() => {
    if (!organization) return;
    const children = organization.organizations ?? [];
    return children.length > 0
      ? children.map((child) => child.id)
      : [organization.id];
  }, [organization]);

  return useQuery({
    queryKey: ["courseBuilderConfigEnabled", orgIds],
    queryFn: async () => {
      // allSettled so one org's failed lookup doesn't fail the whole check —
      // we only need ANY org to have the flag on (e.g. AIDT with 10+ children).
      const results = await Promise.allSettled(
        orgIds.map((id) =>
          configurationService.getOrganizationConfiguration(id)
        )
      );
      return results.some(
        (r) => r.status === "fulfilled" && isCourseBuilderConfigOn(r.value)
      );
    },
    enabled: !!orgIds?.length,
    ...options,
  });
}
